'use client';

import { useEffect, useRef, useState } from 'react';
import {
  formatPhoneWithCountryCode,
  getPhoneDigits,
  parsePhoneInputToNational,
} from '@/core/utils/formatPhone';
import {
  LOGIN_FORM_MIN_NATIONAL_PHONE_DIGITS,
  LOGIN_FORM_OTP_DIGIT_COUNT,
  LOGIN_FORM_RESEND_COOLDOWN_SECONDS,
  LOGIN_FORM_RESEND_COOLDOWN_TICK_MS,
  LOGIN_FORM_OTP_SUCCESS_HOLD_MS,
  LOGIN_FORM_OTP_VERIFY_MIN_DISPLAY_MS,
  LOGIN_FORM_PHONE_DISPLAY_FALLBACK,
} from '@/features/authentication/presentation/composables/constants/login.constants';
import type { LoginFormProps } from '@/features/authentication/presentation/components/LoginForm';
import { LoginFormOtpFeedback, LoginFormStep } from '../enums/login-form.enums';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function useLoginForm({
  authLoading,
  onRequestSendCode,
  onResendCode,
  onVerifyCode,
  onPhoneInputChange,
  onChangeNumberStep,
  onGoogleClick,
  onFacebookClick,
  onVerified,
}: LoginFormProps) {
  const [step, setStep] = useState<LoginFormStep>(LoginFormStep.Phone);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [otpFeedback, setOtpFeedback] = useState<LoginFormOtpFeedback>(LoginFormOtpFeedback.Idle);
  const cooldownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastAutoVerifyCodeRef = useRef<string | null>(null);
  const verifyGenerationRef = useRef(0);

  const otpBusy = otpFeedback === LoginFormOtpFeedback.Verifying || authLoading;
  const isCodeStep = step === LoginFormStep.Code;
  const phoneDisplay = formatPhoneWithCountryCode(phone) || LOGIN_FORM_PHONE_DISPLAY_FALLBACK;
  const phoneFormattedValue = formatPhoneWithCountryCode(phone);
  const phoneDigitsCount = getPhoneDigits(phone).length;
  const sendDisabled = phoneDigitsCount < LOGIN_FORM_MIN_NATIONAL_PHONE_DIGITS || authLoading;

  useEffect(() => {
    if (step !== LoginFormStep.Code) {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
        cooldownIntervalRef.current = null;
      }
      return;
    }
    if (resendCooldownSeconds <= 0) {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
        cooldownIntervalRef.current = null;
      }
      return;
    }
    if (cooldownIntervalRef.current) return;
    cooldownIntervalRef.current = setInterval(() => {
      setResendCooldownSeconds((prev) => {
        if (prev <= 1) {
          if (cooldownIntervalRef.current) {
            clearInterval(cooldownIntervalRef.current);
            cooldownIntervalRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, LOGIN_FORM_RESEND_COOLDOWN_TICK_MS);
    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
        cooldownIntervalRef.current = null;
      }
    };
  }, [step, resendCooldownSeconds]);

  useEffect(() => {
    if (step !== LoginFormStep.Code) {
      lastAutoVerifyCodeRef.current = null;
      setOtpFeedback(LoginFormOtpFeedback.Idle);
      verifyGenerationRef.current += 1;
      return;
    }
    if (code.length < LOGIN_FORM_OTP_DIGIT_COUNT) {
      lastAutoVerifyCodeRef.current = null;
      setOtpFeedback(LoginFormOtpFeedback.Idle);
      verifyGenerationRef.current += 1;
      return;
    }
    if (lastAutoVerifyCodeRef.current === code) return;
    lastAutoVerifyCodeRef.current = code;

    const generationAtStart = verifyGenerationRef.current;

    void (async () => {
      setOtpFeedback(LoginFormOtpFeedback.Verifying);
      const t0 = Date.now();
      const ok = await onVerifyCode(code);
      if (generationAtStart !== verifyGenerationRef.current) return;

      const elapsed = Date.now() - t0;
      const remainingLoading = LOGIN_FORM_OTP_VERIFY_MIN_DISPLAY_MS - elapsed;
      if (remainingLoading > 0) await sleep(remainingLoading);
      if (generationAtStart !== verifyGenerationRef.current) return;

      if (ok) {
        setOtpFeedback(LoginFormOtpFeedback.Success);
        await Promise.resolve();
        await sleep(LOGIN_FORM_OTP_SUCCESS_HOLD_MS);
        if (generationAtStart !== verifyGenerationRef.current) return;
        lastAutoVerifyCodeRef.current = null;
        onVerified?.(phone);
      } else {
        setOtpFeedback(LoginFormOtpFeedback.Error);
      }
    })();
  }, [step, code, onVerifyCode, onVerified, phone]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(parsePhoneInputToNational(e.target.value));
    onPhoneInputChange();
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const digits = getPhoneDigits(phone);
    if (digits.length < LOGIN_FORM_MIN_NATIONAL_PHONE_DIGITS) return;
    const sent = await onRequestSendCode(digits);
    if (!sent) return;
    setStep(LoginFormStep.Code);
    setCode('');
    setOtpFeedback(LoginFormOtpFeedback.Idle);
    setResendCooldownSeconds(LOGIN_FORM_RESEND_COOLDOWN_SECONDS);
  };

  const handleResendCode = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (resendCooldownSeconds > 0 || otpBusy) return;
    const digits = getPhoneDigits(phone);
    if (digits.length < LOGIN_FORM_MIN_NATIONAL_PHONE_DIGITS) return;
    verifyGenerationRef.current += 1;
    setOtpFeedback(LoginFormOtpFeedback.Idle);
    const sent = await onResendCode(digits);
    if (!sent) return;
    setCode('');
    setResendCooldownSeconds(LOGIN_FORM_RESEND_COOLDOWN_SECONDS);
  };

  const handleChangeNumber = (e: React.MouseEvent) => {
    e.preventDefault();
    verifyGenerationRef.current += 1;
    setOtpFeedback(LoginFormOtpFeedback.Idle);
    setStep(LoginFormStep.Phone);
    onChangeNumberStep();
  };

  return {
    authLoading,
    isCodeStep,
    code,
    setCode,
    otpBusy,
    otpFeedback,
    resendCooldownSeconds,
    phoneDisplay,
    phoneFormattedValue,
    sendDisabled,
    handlePhoneChange,
    handleSendCode,
    handleResendCode,
    handleChangeNumber,
    onGoogleClick,
    onFacebookClick,
  };
}
